import React from 'react';
import PropTypes from 'prop-types';
import {Formik} from 'formik';
import isIsraeliIdValid from 'israeli-id-validator';
import Step12View from './Step12-view';
import {checkRequiredValues} from '../../utils/forms/checkRequiredValues';
import {loadOptionsByDDType} from '../../actions/loadOptionsByDDType';
import {ddTypes} from '../../constants/dropdownTypes';
import {errors as E} from '../../constants/errors';
import types from '../../types';
import {checkNextStepAndGo} from '../../utils/checkNextStepAndGo';
import {
  convertISOStringToDateWithTimezoneOffset,
  convertDateWithTimezoneOffsetToEpoch
} from '../../utils/forms/normalizeTime';
import {useIntl} from "react-intl";
import {trackEvent} from "../../utils/trackEvent";

const Step12 = ({
                  submitStep,
                  regType,
                  payload,
                  language,
                  showGlobalErrorMessage,
                  prevCompletedStepSign,
                  prevCompletedStepSignFalse,
                }) => {
  const {
    kidsDetails,
    spouseIncome,
    spouseBirthDate,
    spouseAliyahDate,
    partnerFirstName: firstName,
    partnerLastName: lastName,
    ...restPayload
  } = payload;
  const initialValues = {
    kidsUnder19Exists: false,
    kidsDetails: (kidsDetails)
      ? kidsDetails.map(kid => {
        const {birthDate, ...rest} = kid;
        return {
          birthDate: (birthDate) ? convertISOStringToDateWithTimezoneOffset(birthDate) : null,
          ...rest
        }
      })
      : [{
        name: '',
        ID: '',
        birthDate: null,
        inMyCustody: false,
        receiveAllowance: false,
      }],
    familyStatus: {value: null},
    firstName: firstName || '',
    lastName: lastName || '',
    spouseBirthDate: (spouseBirthDate) ? convertISOStringToDateWithTimezoneOffset(spouseBirthDate) : null,
    spouseID: '',
    spouseAliyahDate: (spouseAliyahDate) ? convertISOStringToDateWithTimezoneOffset(spouseAliyahDate) : null,
    spouseIncome: spouseIncome ? {value: spouseIncome} : '',
    ...restPayload
  };

  const validate = values => {
    const {
      kidsUnder19Exists,
      kidsDetails,
      firstName,
      lastName,
      spouseID,
      spouseBirthDate,
      spouseIncome,
    } = values;
    let errors = {};

    if (kidsUnder19Exists) {
      const kidsDetailsErrors = kidsDetails
        .map(child => {
          const {name, ID, birthDate} = child;
          const childErrors = checkRequiredValues({name, ID, birthDate}, errors);
          if (!isIsraeliIdValid(ID)) {
            childErrors.ID = E.INVALID_ID;
          }

          return (Object.keys(childErrors).length > 0) ? childErrors : undefined;
        });

      errors = (kidsDetailsErrors.filter(Boolean).length > 0)
        ? {...errors, kidsDetails: kidsDetailsErrors}
        : {...errors};
    }

    if (firstName || lastName) {
      errors = checkRequiredValues({spouseBirthDate, spouseID, spouseIncome}, errors);
      if (!isIsraeliIdValid(spouseID)) {
        errors.spouseID = E.INVALID_ID;
      }
    }

    return errors;
  };

  const {formatMessage} = useIntl();

  const handleSubmit = values => {
    const {
      familyStatus,
      firstName,
      lastName,
      spouseIncome,
      kidsDetails,
      spouseBirthDate,
      spouseAliyahDate,
      ...data
    } = values;

    const preparedKidsDetails = kidsDetails.map(kid => {
      const {birthDate, ...rest} = kid;

      return {
        birthDate: convertDateWithTimezoneOffsetToEpoch(birthDate),
        ...rest
      }
    });

    submitStep({
      step: 12,
      data: {
        regType,
        kidsDetails: preparedKidsDetails,
        spouseIncome: spouseIncome.value,
        spouseBirthDate: convertDateWithTimezoneOffsetToEpoch(spouseBirthDate),
        spouseAliyahDate: convertDateWithTimezoneOffsetToEpoch(spouseAliyahDate),
        prevCompletedStepSign,
        ...data
      },
    })
      .then(checkNextStepAndGo)
      .then(() => prevCompletedStepSignFalse())
      .then(() => trackEvent('signup_step',
          {
            step_number: 12,
            step_name_en: 'Income tax',
            step_name_he: formatMessage({id: 'step12.page.title', defaultMessage: ''}),
            regType
          }
        )
      )
      .catch(err => console.log({err}));
  };

  const loadOptions = (inputValue, callback) => {
    loadOptionsByDDType({callback, language, ddType: ddTypes.spouseIncomeItems})
  };

  const handleKidsUnder19ExistsCheckboxChange = newValue => {
    newValue && showGlobalErrorMessage(E.STEP_12_UNDER_19);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {({values, isValid, submitCount}) => (
        <Step12View
          values={values}
          loadOptions={loadOptions}
          isValid={isValid || submitCount === 0}
          onKidsUnder19ExistsCheckboxChange={handleKidsUnder19ExistsCheckboxChange}
        />
      )}
    </Formik>
  )
};

Step12.propTypes = {
  ...types.router,
  regType: PropTypes.string,
  payload: PropTypes.object,
  submitStep: PropTypes.func,
  showGlobalErrorMessage: PropTypes.func,
};

export default Step12;
